import React from 'react';
import Svg, {ClipPath, Defs, G, Line, Polygon, Rect} from 'react-native-svg';

/**
 * The Australian national flag, drawn rather than shipped as an image.
 *
 * It is here because the repo has no flag asset of any kind, and this is the
 * only place one is needed - the mobile field's +61 prefix. Drawing it keeps a
 * binary out of the bundle for all four brands and scales cleanly at the ~20px
 * it renders at.
 *
 * Not verified: the proportions follow the 1:2 construction and the star
 * positions of the published specification, but this is a rendering written in
 * repo, not an asset signed off by design. If the brand kit holds an official
 * SVG, swap it in here and the call sites do not change.
 *
 * The Union Jack's St Patrick saltire is drawn without its counterchange - the
 * red diagonals sit centred on the white rather than offset within them. At the
 * size this renders that is invisible, and the counterchange needs eight
 * clipped triangles to do properly.
 */
// SHORTCUT: BLUE, RED, WHITE and the default height (14, on the `height` prop
// below) are flag construction colours drawn in repo, not an asset signed off
// by design. Ceiling: this component only. Upgrade path: replace with the
// brand kit's official SVG; call sites do not change.
const BLUE = '#012169';
const RED = '#E4002B';
const WHITE = '#FFFFFF';

export interface AustralianFlagProps {
  /** Height in px. Width follows the flag's 1:2 ratio. */
  height?: number;
  testID?: string;
}

export default function AustralianFlag({height = 14, testID}: AustralianFlagProps) {
  return (
    <Svg width={height * 2} height={height} viewBox="0 0 60 30" testID={testID}>
      <Rect width={60} height={30} fill={BLUE} />

      {/* Union Jack, occupying the hoist-side upper quarter. */}
      <Defs>
        <ClipPath id="canton">
          <Rect width={30} height={15} />
        </ClipPath>
      </Defs>
      <G clipPath="url(#canton)">
        <Line x1={0} y1={0} x2={30} y2={15} stroke={WHITE} strokeWidth={3} />
        <Line x1={30} y1={0} x2={0} y2={15} stroke={WHITE} strokeWidth={3} />
        <Line x1={0} y1={0} x2={30} y2={15} stroke={RED} strokeWidth={1} />
        <Line x1={30} y1={0} x2={0} y2={15} stroke={RED} strokeWidth={1} />
        <Line x1={15} y1={0} x2={15} y2={15} stroke={WHITE} strokeWidth={5} />
        <Line x1={0} y1={7.5} x2={30} y2={7.5} stroke={WHITE} strokeWidth={5} />
        <Line x1={15} y1={0} x2={15} y2={15} stroke={RED} strokeWidth={3} />
        <Line x1={0} y1={7.5} x2={30} y2={7.5} stroke={RED} strokeWidth={3} />
      </G>

      {/* Commonwealth Star, in the lower hoist quarter */}
      <Polygon
        points="15.00,18.00 15.87,20.70 18.52,19.69 16.95,22.05 19.39,23.50 16.56,23.75 16.95,26.55 15.00,24.50 13.05,26.55 13.44,23.75 10.61,23.50 13.05,22.05 11.48,19.69 14.13,20.70"
        fill={WHITE}
      />
      {/* Alpha Crucis */}
      <Polygon
        points="45.00,22.86 45.41,24.14 46.68,23.66 45.93,24.79 47.09,25.48 45.74,25.59 45.93,26.93 45.00,25.95 44.07,26.93 44.26,25.59 42.91,25.48 44.07,24.79 43.32,23.66 44.59,24.14"
        fill={WHITE}
      />
      {/* Beta Crucis */}
      <Polygon
        points="37.50,10.98 37.91,12.27 39.18,11.79 38.43,12.91 39.59,13.60 38.24,13.72 38.43,15.06 37.50,14.08 36.57,15.06 36.76,13.72 35.41,13.60 36.57,12.91 35.82,11.79 37.09,12.27"
        fill={WHITE}
      />
      {/* Gamma Crucis */}
      <Polygon
        points="45.00,4.11 45.41,5.39 46.68,4.91 45.93,6.04 47.09,6.73 45.74,6.84 45.93,8.18 45.00,7.20 44.07,8.18 44.26,6.84 42.91,6.73 44.07,6.04 43.32,4.91 44.59,5.39"
        fill={WHITE}
      />
      {/* Delta Crucis */}
      <Polygon
        points="52.50,12.23 52.91,13.52 54.18,13.04 53.43,14.16 54.59,14.85 53.24,14.97 53.43,16.31 52.50,15.33 51.57,16.31 51.76,14.97 50.41,14.85 51.57,14.16 50.82,13.04 52.09,13.52"
        fill={WHITE}
      />
      {/* Epsilon Crucis, the only five-pointed star */}
      <Polygon
        points="48.00,16.93 48.33,17.73 49.19,17.79 48.53,18.35 48.73,19.19 48.00,18.74 47.27,19.19 47.47,18.35 46.81,17.79 47.67,17.73"
        fill={WHITE}
      />
    </Svg>
  );
}
